if (!customElements.get('ae-order-tracking')) {
  customElements.define('ae-order-tracking', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.texts = JSON.parse(this.querySelector('[data-tracking-texts]').textContent);
      this.form = this.querySelector('form');
      this.result = this.querySelector('.ae-track__result');
      this.error = this.querySelector('.ae-track__error');
      this.button = this.querySelector('[type=submit]');
      const number = new URL(location.href).searchParams.get('order');
      if (number) this.form.elements.orderNumber.value = number.slice(0, 40);
      this.form.addEventListener('submit', event => {event.preventDefault(); this.lookup();});
    }
    t(key) {return this.texts[key] || key;}
    el(tag, className, text) {
      const el = document.createElement(tag);
      if (className) el.className = className;
      if (text) el.textContent = text;
      return el;
    }
    date(value, time = false) {
      return new Intl.DateTimeFormat(this.dataset.lang || 'fr', {timeZone: 'Europe/Zurich', day: 'numeric', month: 'long', ...(time ? {hour: '2-digit', minute: '2-digit'} : {})}).format(new Date(value));
    }
    async lookup() {
      this.error.hidden = true;
      this.result.hidden = true;
      const orderNumber = this.form.elements.orderNumber.value.trim();
      const accessCode = this.form.elements.accessCode.value.trim();
      if (!orderNumber || !accessCode) {this.showError('missing_fields'); return;}
      this.button.disabled = true;
      this.button.firstElementChild.textContent = this.t('loading');
      this.form.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.dataset.endpoint, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({orderNumber, accessCode, lang: this.dataset.lang}), cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer', signal: AbortSignal.timeout(20000)});
        const data = await response.json();
        if (!data.ok) {this.showError(data.message); return;}
        this.render(data);
      } catch (_) {this.showError('unavailable');}
      finally {this.button.disabled = false; this.button.firstElementChild.textContent = this.t('submit'); this.form.removeAttribute('aria-busy');}
    }
    showError(key) {this.error.textContent = this.t(['missing_fields', 'not_found', 'rate_limited'].includes(key) ? key : 'unavailable'); this.error.hidden = false;}
    render(data) {
      this.error.hidden = true;
      this.result.replaceChildren();
      const head = this.el('div', 'ae-track__result-head');
      head.append(this.el('h2', '', `${this.t('order')} #${data.order.number}`));
      const allOfficial = data.shipments.length && data.shipments.every(s => s.mode === 'official');
      head.append(this.el('span', 'ae-track__badge', data.order.cancelled ? this.t('cancelled') : allOfficial ? this.t('official') : data.shipments[0]?.mode === 'preparing' ? this.t('preparing') : this.t('estimated')));
      this.result.append(head);
      const identity = this.el('div', 'ae-track__identity');
      if (data.order.customerName) identity.append(this.el('p', 'ae-track__identity-name', data.order.customerName));
      const destination = [data.order.postalCode, data.order.city].filter(Boolean).join(' ');
      if (destination) identity.append(this.el('p', 'ae-track__identity-address', destination));
      if (identity.childElementCount) this.result.append(identity);
      if (data.order.partial) this.result.append(this.el('p', 'ae-track__notice', this.t('partial')));
      if (data.order.cancelled) this.result.append(this.el('p', 'ae-track__notice', this.t('cancelled_text')));
      data.shipments.forEach(shipment => {
        const card = this.el('section', 'ae-track__shipment');
        const header = this.el('div', 'ae-track__shipment-header');
        const current = shipment.timeline?.[0];
        header.append(this.el('h3', '', data.shipments.length > 1 ? `${this.t('shipment')} ${shipment.index}` : shipment.mode === 'official' ? this.t('official') : current?.title || this.t('preparing')));
        if (shipment.mode !== 'estimated') header.append(this.el('p', 'ae-track__notice', this.t(shipment.mode === 'official' ? 'official_notice' : 'preparing_text')));
        if (shipment.deliveryMin && shipment.deliveryMax) {
          const overdue = new Date(shipment.deliveryMax).getTime() + 86400000 < Date.now();
          header.append(this.el('span', `ae-track__delivery${overdue ? ' ae-track__overdue' : ''}`, overdue ? this.t('overdue') : `${this.t('delivery')} ${this.date(shipment.deliveryMin)} – ${this.date(shipment.deliveryMax)}`));
        }
        card.append(header);
        if (shipment.mode === 'official') this.renderOfficial(card, shipment);
        else this.renderTimeline(card, shipment.timeline || []);
        this.result.append(card);
      });
      this.result.hidden = false;
      this.result.focus({preventScroll: true});
      this.result.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
    }
    renderTimeline(card, timeline) {
      const list = this.el('ol', 'ae-track__timeline');
      timeline.forEach(step => {
        const item = this.el('li', `ae-track__step${step.state === 'current' ? ' ae-track__step--current' : ''}`);
        if (step.state === 'current') item.setAttribute('aria-current', 'step');
        const dot = this.el('span', 'ae-track__dot'); dot.setAttribute('aria-hidden', 'true');
        item.append(dot, this.el('h4', '', step.title), this.el('p', '', step.text));
        if (step.date) {
          const time = this.el('time', '', `${this.date(step.date, true)}${step.estimated ? ` · ${this.t('estimated_time')}` : ''}`);
          time.dateTime = step.date; item.append(time);
        }
        list.append(item);
      });
      card.append(list);
    }
    renderOfficial(card, shipment) {
      const content = this.el('div', 'ae-track__official');
      shipment.tracking.forEach(tracking => {
        const row = this.el('div', 'ae-track__number'); row.append(this.el('code', '', tracking.number));
        const copy = this.el('button', 'ae-track__copy', this.t('copy')); copy.type = 'button';
        copy.addEventListener('click', async () => {try {await navigator.clipboard.writeText(tracking.number); copy.textContent = this.t('copied');} catch (_) {copy.textContent = this.t('copy_fallback');}});
        row.append(copy); content.append(row);
        tracking.links.forEach(link => {
          let url; try {url = new URL(link.url);} catch (_) {return;}
          if (url.protocol !== 'https:') return;
          const anchor = this.el('a', 'ae-track__carrier', `${this.t('carrier')} ${link.carrier} ↗`);
          anchor.href = url.href; anchor.target = '_blank'; anchor.rel = 'noopener noreferrer'; content.append(anchor);
        });
      });
      card.append(content);
    }

  });
}
